import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import CampaignService from '../../../services/CampaignService';
import { getLogo } from '../../../services/LogoStorageService';
import { formatMoney } from '../../../utils/currency';

const WelcomeTicketPreview = ({ formData, logoUrl, companyName = 'Empresa' }) => {
  const discountText = formData.discountType === 'amount'
    ? `${formatMoney(Number(formData.discountAmount || 0))} de descuento`
    : `${Number(formData.discountPercentage || 0)}% de descuento`;

  return (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
      <Box
        sx={{
          width: 250,
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          px: 1.5,
          py: 2,
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center'
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={companyName}
            sx={{ display: 'block', maxWidth: 110, maxHeight: 52, objectFit: 'contain', mx: 'auto', mb: 2 }}
          />
        ) : (
          <Typography sx={{ fontFamily: 'serif', fontSize: 28, lineHeight: 1, mb: 2 }}>{companyName}</Typography>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Cupón de bienvenida</Typography>
        <Typography sx={{ fontSize: 11 }}>Cliente: Martin Perez</Typography>
        <Typography sx={{ fontSize: 11, mb: 1 }}>Fecha: 22/07/2026 18:14</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace', mb: 1 }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>¡Gracias por registrarte!</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>{discountText}</Typography>
        <Typography sx={{ fontSize: 11 }}>Código de descuento</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>BIENVENIDA</Typography>
        <Typography sx={{ fontSize: 11, mb: 1.5 }}>Válido por única vez</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 12 }}>¡Te esperamos!</Typography>
      </Box>
    </Box>
  );
};

const WelcomeCampaignTab = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewLogoUrl, setPreviewLogoUrl] = useState(null);
  const [formData, setFormData] = useState({
    enabled: false,
    printTicket: false,
    sendEmail: true,
    salesChannel: 'both',
    subject: '',
    htmlContent: '',
    discountPercentage: 0,
    discountAmount: 0,
    discountType: 'percentage' // 'percentage' or 'amount'
  });

  // Cargar configuración de campaña de bienvenida
  const loadCampaignConfig = useCallback(async () => {
    console.log('[WelcomeCampaignTab] Loading campaign config - Starting...');
    try {
      setLoading(true);
      setError(null);
      const response = await CampaignService.getWelcomeCampaign();
      console.log('[WelcomeCampaignTab] Full response:', response);
      console.log('[WelcomeCampaignTab] Response data:', response.data);
      
      // Manejar estructura anidada: response.data.data.data o response.data.data
      const campaignData = response.data?.data?.data || response.data?.data || response.data;
      console.log('[WelcomeCampaignTab] Campaign data extracted:', campaignData);
      
      if (campaignData) {
        setFormData({
          enabled: campaignData.enabled ?? false,
          printTicket: campaignData.printTicket === true,
          sendEmail: campaignData.sendEmail !== false,
          salesChannel: campaignData.salesChannel || 'both',
          subject: campaignData.subject || '',
          htmlContent: campaignData.htmlContent || '',
          discountPercentage: campaignData.discountPercentage || 0,
          discountAmount: campaignData.discountAmount || 0,
          discountType: campaignData.discountType || 'percentage'
        });
        console.log('[WelcomeCampaignTab] Form data updated successfully');
      }
    } catch (err) {
      console.error('[WelcomeCampaignTab] Error loading welcome campaign:', err);
      console.error('[WelcomeCampaignTab] Error status:', err.response?.status);
      // Si es 404, la campaña no existe aún, usar valores por defecto
      if (err.response?.status !== 404) {
        setError(t('error_loading_welcome_campaign') || 'Error al cargar la campaña de bienvenida');
      } else {
        console.log('[WelcomeCampaignTab] Campaign not found (404) - using defaults');
      }
    } finally {
      setLoading(false);
      console.log('[WelcomeCampaignTab] Loading campaign config - Completed');
    }
  }, [t]);

  useEffect(() => {
    loadCampaignConfig();
  }, [loadCampaignConfig]);

  useEffect(() => {
    let mounted = true;
    const loadPreviewLogo = async () => {
      try {
        const logo = await getLogo();
        if (mounted) setPreviewLogoUrl(logo || null);
      } catch (err) {
        if (mounted) setPreviewLogoUrl(null);
      }
    };

    loadPreviewLogo();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validaciones solo si la campaña está habilitada
    if (formData.enabled) {
      if (formData.sendEmail && (!formData.subject || !formData.subject.trim())) {
        setError(t('subject_required') || 'El asunto es obligatorio');
        return;
      }

      if (formData.sendEmail && (!formData.htmlContent || !formData.htmlContent.trim())) {
        setError(t('content_required') || 'El contenido del correo no puede estar vacío');
        return;
      }

      const hasAnyChannelEnabled = formData.printTicket || formData.sendEmail;
      if (hasAnyChannelEnabled && formData.discountType === 'percentage' && (formData.discountPercentage <= 0 || formData.discountPercentage > 100)) {
        setError(t('invalid_discount_percentage') || 'El porcentaje de descuento debe estar entre 1 y 100');
        return;
      }

      if (hasAnyChannelEnabled && formData.discountType === 'amount' && formData.discountAmount <= 0) {
        setError(t('invalid_discount_amount') || 'El monto de descuento debe ser mayor a 0');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const campaignData = {
        enabled: formData.enabled,
        printTicket: formData.printTicket,
        sendEmail: formData.sendEmail,
        salesChannel: formData.salesChannel || 'both',
        subject: formData.subject.trim(),
        htmlContent: formData.htmlContent.trim(),
        discountPercentage: formData.discountPercentage || 0,
        discountAmount: formData.discountAmount || 0,
        discountType: formData.discountType
      };

      await CampaignService.saveWelcomeCampaign(campaignData);

      SnackbarUtils.success(t('welcome_campaign_saved_successfully') || 'Campaña de bienvenida guardada exitosamente');
    } catch (err) {
      console.error('Error saving welcome campaign:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errorMessage || (t('error_saving_welcome_campaign') || 'Error al guardar la campaña de bienvenida'));
      SnackbarUtils.error(errorMessage || (t('error_saving_welcome_campaign') || 'Error al guardar la campaña de bienvenida'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              {t('welcome_campaign_configuration') || 'Configuración de Campaña de Bienvenida'}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
              }
              label={t('enable_welcome_campaign') || 'Habilitar campaña de bienvenida'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('welcome_campaign_description') || 'Configura la campaña automática para nuevos clientes cuando se registren.'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        {formData.enabled && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Dónde aplica
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={formData.salesChannel || 'both'}
                  onChange={(e) => handleChange('salesChannel', e.target.value)}
                >
                  <FormControlLabel value="both" control={<Radio />} label="Ambos" />
                  <FormControlLabel value="local" control={<Radio />} label="Solo local" />
                  <FormControlLabel value="web" control={<Radio />} label="Solo web" />
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                Define si la campaña se evalúa en ventas del local, compras web o ambos canales.
              </Typography>
              <Divider sx={{ mt: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Ticket impreso
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.printTicket}
                    onChange={(e) => handleChange('printTicket', e.target.checked)}
                  />
                }
                label="Imprimir cupón de bienvenida al registrar el cliente"
              />
              <Typography variant="body2" color="text.secondary">
                Si está activo, la campaña queda configurada para emitir un cupón impreso de bienvenida.
              </Typography>
              <Divider sx={{ mt: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Envío de correo
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sendEmail}
                    onChange={(e) => handleChange('sendEmail', e.target.checked)}
                  />
                }
                label="Enviar correo de bienvenida al registrar el cliente"
              />
              <Typography variant="body2" color="text.secondary">
                Si está activo, los clientes nuevos reciben el correo de bienvenida con su código de descuento.
              </Typography>
              <Divider sx={{ mt: 2 }} />
            </Grid>

            {formData.printTicket && (
              <Grid item xs={12}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Contenido del ticket
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      El cupón impreso usa el descuento configurado y genera un código único de bienvenida para el cliente.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mb: 2 }}>
                      Vista previa
                    </Typography>
                    <WelcomeTicketPreview formData={formData} logoUrl={previewLogoUrl} />
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 2 }} />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('subject')}
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder={t('welcome_email_subject_placeholder') || '¡Bienvenido a nuestra tienda!'}
                disabled={!formData.sendEmail}
              />
            </Grid>

            {formData.sendEmail && (
            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                  <Tab label={t('edit_content')} />
                  <Tab label={t('preview')} />
                </Tabs>
              </Box>

              {activeTab === 0 && (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={16}
                    label={t('email_content')}
                    value={formData.htmlContent}
                    onChange={(e) => handleChange('htmlContent', e.target.value)}
                    placeholder={t('html_content_placeholder')}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
              )}

              {activeTab === 1 && (
                <Paper sx={{ p: 2, minHeight: 300, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    {formData.subject || t('email_preview')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    dangerouslySetInnerHTML={{ __html: formData.htmlContent || t('no_content') }}
                    sx={{
                      '& img': { maxWidth: '100%' },
                      '& a': { color: 'primary.main' }
                    }}
                  />
                </Paper>
              )}
            </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">{t('welcome_discount') || 'Descuento de bienvenida'}</FormLabel>
                <RadioGroup
                  row
                  value={formData.discountType}
                  onChange={(e) => handleChange('discountType', e.target.value)}
                >
                  <FormControlLabel 
                    value="percentage" 
                    control={<Radio />} 
                    label={t('percentage_discount') || 'Descuento por porcentaje'} 
                  />
                  <FormControlLabel 
                    value="amount" 
                    control={<Radio />} 
                    label={t('amount_discount') || 'Descuento por monto'} 
                  />
                </RadioGroup>
              </FormControl>

              {formData.discountType === 'percentage' ? (
                <TextField
                  fullWidth
                  type="number"
                  label={t('discount_percentage') || 'Porcentaje de descuento (%)'}
                  value={formData.discountPercentage}
                  onChange={(e) => handleChange('discountPercentage', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  helperText={t('discount_percentage_helper') || 'Ingresa un valor entre 1 y 100'}
                  sx={{ mt: 2 }}
                />
              ) : (
                <TextField
                  fullWidth
                  type="number"
                  label={t('discount_amount') || 'Monto de descuento'}
                  value={formData.discountAmount}
                  onChange={(e) => handleChange('discountAmount', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={t('discount_amount_helper') || 'Ingresa el monto del descuento'}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: '#E3F2FD', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('automatic_discount_code') || 'Código de descuento automático'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {formData.discountType === 'percentage' 
                      ? t('automatic_discount_code_message_percentage', { percentage: formData.discountPercentage }) || 
                        `Se generará automáticamente un código de descuento del ${formData.discountPercentage}% que se incluirá en el correo de bienvenida.`
                      : t('automatic_discount_code_message_amount', { amount: formData.discountAmount }) || 
                        `Se generará automáticamente un código de descuento de ${formatMoney(formData.discountAmount)} que se incluirá en el correo de bienvenida.`
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                    {formData.sendEmail
                      ? (t('discount_tag_info') || 'Para incluir el código de descuento en el correo, utiliza el tag {{discount}} en el contenido HTML.')
                      : 'El código se generará para los canales habilitados de esta campaña.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={getIcon('save')}
              onClick={handleSubmit}
              disabled={saving || (formData.enabled && formData.sendEmail && (!formData.subject || !formData.htmlContent))}
            >
              {saving 
                ? (t('saving') || 'Guardando...') 
                : (t('save') || 'Guardar')
              }
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WelcomeCampaignTab;
